/**
 * OBS Order React component for `GET /ibe/:lodgit_desk_id` page.
 * 
 * NOTES:
 * - For example, adding <%= javascript_pack_tag 'order' %> to the header of a layout file will embed this component.
 * - Use it as a reference for future components per page.
 * - Note that components are loaded ONLY from `app/javascript/packs` directory.
 */

import React, { useState, useEffect, Fragment } from 'react'
import ReactDOM from 'react-dom/client'
import moment, { Moment } from "moment"
import DatePicker from '../portal/responsive/host/order/datepicker'
import OvernightStaysCaption from '../portal/responsive/host/order/overnight_stays_caption'
import ErrorCaption from '../portal/responsive/host/order/error_caption'

const DEFAULT_LOCALE = 'en'

/**
 * It get valid/available locale code as well as its corresponding translations.
 * In case of translations are not available then default ones are returned.
 */
function get_locale(data, inputLocale) {
    const locale = (data && inputLocale && data[inputLocale]) ? inputLocale : DEFAULT_LOCALE
    return locale
}


/** Main OBS Search component (entrypoint component) */
const ObsSearchForm = ({ dataset }) => {

    const [translations,setTranslationsUsed]=useState( {
            errorOccured: '',
            minArrivalDateToBig: '',
            noArrivalWeekdays: '',
            noDepatureWeekdays: '',
            selectArrivalDate: '',
            selectDepartureDate: '',
            staysPlural: '{count}',
            staysSingular: '',
            buttonReset: '',
            buttonDone: ''},);

    const [locale,setLocale]=useState(DEFAULT_LOCALE);

    const getData = () => {
        if (translations.errorOccured === '') {
                fetch('/ibe/resource/translate_react_calendar'
                    , {
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        }
                    }
                )
                    .then(function (response) {
                        return response.json();
                    })
                    .then(function (myJson) {
                        let dsLocale = dataset.locale.slice(0, 2).toLowerCase()
                        let locale = get_locale(myJson, dsLocale, DEFAULT_LOCALE)
                        moment.locale(locale)
                        // *****************************************************
                        // Handle valid user's locale
                        setLocale(locale)
                        setTranslationsUsed(myJson[locale])
                        getLocale(locale)
                    });

            }
        }
    // load translation data from server once
    useEffect(() => {
        getData()
    }, []);


    // dataset: it represents the input data for the custom attributes
    // added to the current element (date-picker root element).
    // It always returns an `DOMStringMap` object


    const format_arrival_date = (loc) => {
        let arrival_date = moment()
        if (dataset.arrival !== "") {
            // Transform arrival_date date to moment
            arrival_date = moment(dataset.arrival, "YYYY-MM-DD")
        }
        return arrival_date
    }

    const format_departure_data = (loc) => {
        let departure_date = moment().add(1,'days')
        if (dataset.departure !== "") {
            // Transform departure date to moment
            departure_date = moment(dataset.departure, "YYYY-MM-DD")
        }
        return departure_date
    }

    const getLocale = loc => {
        if (translations.errorOccured !== '') {
            if (loc === 'en') loc = 'en-GB'
            require(`date-fns/locale/${loc}/index.js`)
        }
    }

    const renderCalendar = (restrictions, arrival_date, departure_date, overnightStaysCaption, setOvernightStaysCaption) => (
        (translations.errorOccured === '')
            ?   <Fragment>
                    <ErrorCaption
                        caption='...'
                    />
                </Fragment>
            : <Fragment>
                <DatePicker
                    restrictions={restrictions}
                    arrival_date={arrival_date}
                    departure_date={departure_date}
                    translations={translations}
                    setOvernightStaysCaption={setOvernightStaysCaption}
                />
                <OvernightStaysCaption caption={overnightStaysCaption} />
            </Fragment>
    )

    try {
        // Handle restrictions object
        // *****************************************************
        const restrictions = {}
        const dataRestrictions = (dataset.restrictions ? JSON.parse(dataset.restrictions) : {}) 
        
        // *****************************************************
        // Arrival and departure Date
        let arrival_date = format_arrival_date(locale)
        let departure_date = format_departure_data(locale)

        // *****************************************************
        // Minimum arrival date
        restrictions.minimum_arrival_date = moment()
        if (dataRestrictions.minimum_arrival_date_utc) {
            // Transform date (string) to moment
            const date = Date.parse(dataRestrictions.minimum_arrival_date_utc)
            restrictions.minimum_arrival_date = moment(date)

            // Check if the minimum arrival date is lower than 2 years from now
            const inTwoYears = moment().add(2, "years")
            if (restrictions.minimum_arrival_date.isSameOrAfter(inTwoYears, "day")) {
                const today = moment()
                const duration = moment.duration(restrictions.minimum_arrival_date.diff(today))
                const years = Math.round(duration.asYears())

                return (
                    <Fragment>
                        <ErrorCaption
                            caption={translations.minArrivalDateToBig.replace("{count}", years)}
                        />
                    </Fragment>
                )
            }
        }
       
        // *****************************************************
        // Weekday restrictions
        if (dataRestrictions.weekday_restrictions_application_date_utc) {
            // Transform date (string) to moment
            const date = Date.parse(dataRestrictions.weekday_restrictions_application_date_utc)
            restrictions.weekday_restrictions_application_date = moment(date)

            // Check for allowed arrival weekdays
            if (dataRestrictions.allowed_arrival_weekdays.length > 0 ) {
                restrictions.allowed_arrival_weekdays = dataRestrictions.allowed_arrival_weekdays
            } else {
                return (
                    <Fragment>
                        <ErrorCaption
                            caption={translations.noArrivalWeekdays}
                        />
                    </Fragment>
                )
            }

            // Check for allowed departure weekdays
            if (dataRestrictions.allowed_departure_weekdays.length > 0 ) {
                restrictions.allowed_departure_weekdays = dataRestrictions.allowed_departure_weekdays
            } else {
                return (
                    <Fragment>
                        <ErrorCaption
                            caption={translations.noDepatureWeekdays}
                        />
                    </Fragment>
                )
            }
        }

        // *****************************************************
        // Minlos
        restrictions.minlos = 1
        if (dataRestrictions.minlos_restrictions_application_date_utc) {
            const date = Date.parse(dataRestrictions.minlos_restrictions_application_date_utc)
            restrictions.minlos_restrictions_application_date = moment(date)

            if (dataRestrictions.saisonal_minlos_settings_available === false) {
                if (dataRestrictions.minlos > 0) restrictions.minlos = dataRestrictions.minlos
            }
        }

        // Overnight Stays Caption state
        const [ overnightStaysCaption, setOvernightStaysCaption  ] = useState(dataset.overnight_stays_caption || '')

        return renderCalendar(restrictions, arrival_date, departure_date, overnightStaysCaption, setOvernightStaysCaption)
    } catch (error) {
        console.error(error)

        return (
            <Fragment>
                <ErrorCaption
                    caption={translations.errorOccured}
                />
            </Fragment>
        )
    }
}

document.addEventListener('DOMContentLoaded', () => {
    // Embed application component into the DOM when is loaded
    const root = document.getElementById('obs-search-form')
    if (root) {
        const form = ReactDOM.createRoot(root)
        form.render(<ObsSearchForm dataset={root.dataset} />)
    }
})
